import { type FC, type PropsWithChildren, createContext, useCallback, useContext, useEffect, useState } from "react";
import wretch from "wretch";
import { buildApiHeaders } from "../http/apiHeaders";
import { withErrorBody } from "../http/errorBody";
import { type AccessDeniedReason, getAccessDeniedReason } from "../http/types/accessControl";
import { type UILanguageCode } from "../types";
import { useAppContext } from "./appContext";

type Session = {
  jwtToken: string;
  identity: Identity;
};

type Claims = {
  identity: Identity;
  exp: number;
};

export type Identity = {
  id: string;
  firstName?: string;
  lastName?: string;
  name: string;
  job: string;
  msGraphJobTitle: string;
  email: string;
  language: UILanguageCode;
};

export type AuthContextType = {
  identity: Identity | null | undefined;
  init: (redirect: boolean) => void;
  jwtToken: string | null | undefined;
  permissions: {
    canAccess: boolean;
    // test users (tester role)
    canSendFeedback: boolean;
    admin: {
      canManageUsers: boolean;
      canManageClients: boolean;
      canManageCategories: boolean;
      canManageModules: boolean;
      canManageProjects: boolean;
      canManageCalculations: boolean;
      canManageItems: boolean;
      canManageAccessControl: boolean;
      canManageUsageStatistics: boolean;
      canManageGeoBlocking: boolean;
    };
  };
  isLoading: boolean;
  isExpired: boolean;
  // The startup permissions call failed for a reason other than an access denial (e.g. a 5xx): the
  // permission-gated routes (the admin ones) are then missing, see AuthenticatedAppShell.
  permissionsLoadFailed: boolean;
  // Set when the backend's access check refused this app for the current user, either by the
  // startup permissions call or by any later API call (see useApi), e.g. when an admin revokes
  // access while the user is inside. AccessGate then replaces the whole app with AccessDeniedPage.
  accessDenied: AccessDeniedReason;
  denyAccess: (reason: NonNullable<AccessDeniedReason>) => void;
  // Re-runs the access check; resolves to the denial reason, null once access is granted.
  recheckAccess: () => Promise<AccessDeniedReason>;
  logout: () => void;
  expire: () => void;
};

const AuthContext = createContext<AuthContextType>(undefined!);

const getExpirationTime = (session: Session): number => {
  const claims: Claims = JSON.parse(atob(session.jwtToken.split(".")[1] ?? ""));
  return claims.exp * 1000; // Convert to milliseconds
};

// Function to check if JWT is expired
const isSessionValid = (session?: Session): boolean => {
  if (!session) return false; // Token doesn't exist
  return Date.now() < getExpirationTime(session); // Check if token is not expired
};

export interface RothoSSOSettings {
  clientId: string;
  iamUrl: string;
}

type AuthProviderProps = PropsWithChildren<{
  settings: RothoSSOSettings;
}>;

const AuthProvider: FC<AuthProviderProps> = ({ children, settings }: AuthProviderProps) => {
  const { scope } = useAppContext();

  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpired, setIsExpired] = useState(false);

  const [permissions, setPermissions] = useState<string[]>();
  const [permissionsLoadFailed, setPermissionsLoadFailed] = useState(false);
  const [accessDenied, setAccessDenied] = useState<AccessDeniedReason>(null);

  const logout = useCallback(() => {
    window.location.href = `${settings.iamUrl}/logout?redirect_uri=${encodeURIComponent(window.location.href)}`;
  }, [settings.iamUrl]);

  const expire = useCallback(() => setIsExpired(true), []);

  const denyAccess = useCallback((reason: NonNullable<AccessDeniedReason>) => setAccessDenied(reason), []);

  const handleTokenExpiry = () => {
    setIsExpired(true);
    setIsLoading(false);
  };

  const loadPermissions = (): Promise<AccessDeniedReason> => {
    if (!session?.jwtToken) {
      setIsLoading(false);
      return Promise.resolve(null);
    }
    return (
      wretch()
        .customError(withErrorBody)
        .options({ credentials: "include" })
        // Same headers as every other API call, the app scope included: without it the access
        // check would be the main app's, not this app's (see buildApiHeaders).
        .headers(buildApiHeaders(session.jwtToken, scope))
        .url("/api/me/permissions")
        .get()
        .json<string[]>()
        .then((p): AccessDeniedReason => {
          setAccessDenied(null);
          setPermissionsLoadFailed(false);
          setPermissions(p);
          return null;
        })
        .catch((err): AccessDeniedReason => {
          const reason = getAccessDeniedReason(err);
          if (reason) {
            setAccessDenied(reason);
          } else {
            setPermissionsLoadFailed(true);
            console.log(err);
          }
          return reason;
        })
        .finally(() => setIsLoading(false))
    );
  };

  const whoAmI = (redirect: boolean) =>
    wretch()
      .options({ credentials: "include" })
      .url(`${settings.iamUrl}/api/v1/sessions/whoami`)
      .get()
      .json<Session>()
      .then(setSession)
      .catch((err) => {
        if (redirect && err?.status === 401 && isLoading) {
          const params = new URLSearchParams(window.location.search);
          const redirectUri = params.get("redirect_uri");
          const uri = encodeURIComponent(redirectUri ? redirectUri : window.location.href);
          window.location.href = `${settings.iamUrl}?redirect_uri=${uri}&client_id=${settings.clientId}`;
        }
      });

  // useEffect(() => {
  //   whoAmI();
  // }, []);

  useEffect(() => {
    if (session?.jwtToken) {
      if (isSessionValid(session)) {
        loadPermissions();
      } else {
        handleTokenExpiry();
      }
    }
  }, [session?.jwtToken]);

  const hasPermission = (permission: string) => permissions?.includes(permission) ?? false;

  const contextValue: AuthContextType = {
    identity: session?.identity,
    jwtToken: session?.jwtToken,
    init: (redirect: boolean) => whoAmI(redirect),
    permissions: {
      canAccess: !permissions?.[0],
      canSendFeedback: hasPermission("feedback:send"),
      admin: {
        canManageUsers: hasPermission("users:all"),
        canManageClients: hasPermission("clients:all"),
        canManageModules: hasPermission("modules:all"),
        canManageCategories: hasPermission("categories:all"),
        canManageProjects: hasPermission("projects:all"),
        canManageCalculations: hasPermission("calculations:all"),
        canManageItems: hasPermission("items:all"),
        canManageAccessControl: hasPermission("access_control:all"),
        canManageUsageStatistics: hasPermission("usage_statistics:all"),
        canManageGeoBlocking: hasPermission("geo_blocking:all"),
      },
    },
    isExpired,
    isLoading,
    permissionsLoadFailed,
    accessDenied,
    denyAccess,
    recheckAccess: loadPermissions,
    logout,
    expire,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  return useContext(AuthContext);
};

export default AuthProvider;
